// Angular imports
import { Component, EventEmitter, OnInit, Input, Output, ViewChild } from '@angular/core';

// Third party imports
import notify from 'devextreme/ui/notify';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Component imports
import { WaiveInvoiceConfirmationComponent } from './waive-invoice-confirmation/waive-invoice-confirmation.component';

// Constant imports
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';

// Service imports
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { ContextManagerService } from '../../../../_services/data-services/context-manager.service';
import { InvoiceDetailsService } from '../../_services/account-receivable/invoice-details.service';
import { ARCreditApplicationService } from '../../_services/account-receivable/ar-credit-application.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { UserService } from '../../../../_services/data-services/user.service';

// Model imports
import { PropertyCommunityInfoResultDto } from '../../../../_models/property-community-info-result-dto';
import { ARInvoiceResultDto } from '../../_models/account-receivable/ar-invoice/ar-invoice-result-dto';
import { ARInvoiceDetailParameterDto } from '../../_models/account-receivable/ar-invoice/ar-invoice-detail-parameter-dto';
import { ARCreditApplicationsResultDto } from '../../_models/account-receivable/credit-applications/ar-credit-applications-result-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { DimensionResultDto } from '../../_models/common/dimensions/dimension-result-dto';

@Component({
    selector: 'app-ar-invoice-details',
    templateUrl: './ar-invoice-details.component.html',
    styleUrls: ['./ar-invoice-details.component.scss']
})
export class ARInvoiceDetailsComponent implements OnInit {
    @ViewChild('waiveInvoiceConfirmation', { static: false })
    waiveInvoiceConfirmation: WaiveInvoiceConfirmationComponent;
    @Input() visible: boolean;
    @Input() invoiceDetailParam: ARInvoiceDetailParameterDto;
    @Input() invoiceId: any; // in statement component invoiceId type is string
    @Input() managementCompanyId: number;
    @Output() onInvoiceDetailClose = new EventEmitter<boolean>();
    @Output() onSuccess: EventEmitter<boolean> = new EventEmitter();

    isInternalUser = false;
    loadingVisible: boolean;
    hasOtherDimensions: boolean;
    currencyFormat: any;
    dateFormat: any;
    ownerDashboardUrl: string;
    invoiceDetails: ARInvoiceResultDto;
    popUpTitle: string;
    invoiceEditorPopupTitle: string;

    displayTransactionLogPopUp = false;
    transactionLogTitle: string;
    transactionLogResultData: TransactionLogResultDto[] = [];
    selectedContext: any = {};

    showWriteOffInvoice = false;
    showReverseEditor = false;
    showInvoiceEditor = false;

    showCreditApplication = false;
    transactionType = 'Invoice';
    creditApplicationData: ARCreditApplicationsResultDto[] = [];

    showManualCreditApplication = false;

    historyIcon = IconConstants.HISTORY;
    closeIcon = IconConstants.CLOSE;
    deleteIcon = IconConstants.DELETE;
    editIcon = IconConstants.EDIT;
    printIcon = IconConstants.PRINT;
    iconKey = IconConstants.KEY;
    iconReverse = IconConstants.REVERSE;
    iconWriteOff = IconConstants.WRITE_OFF;

    constructor(
        private readonly precisionService: PrecisionService,
        private readonly serverErrorService: ServerErrorService,
        private readonly contextManagerService: ContextManagerService,
        private readonly invoiceDetailsService: InvoiceDetailsService,
        private readonly arCreditApplicationService: ARCreditApplicationService,
        private readonly helperService: HelperService,
        private readonly userService: UserService
    ) {
        this.isInternalUser = this.userService.isInternal();
    }

    ngOnInit(): void {
        this.dateFormat = StringFormatConstants.dateFormat;
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        if (!!this.invoiceDetailParam) {
            this.popUpTitle = 'AR Invoice ' + this.invoiceDetailParam.invoiceId;
        }
        this.show();
        this.loadInvoiceDetails();
    }

    show(): void {
        this.visible = true;
    }

    onShown(e): void {
        e.component.repaint();
    }

    onHidden(refresh: boolean): void {
        this.ownerDashboardUrl = null;
        this.invoiceId = null;
        this.invoiceDetails = null;
        this.visible = false;
        this.invoiceDetailParam = null;
        this.popUpTitle = '';
        this.onInvoiceDetailClose.emit(refresh);
    }

    isNotRequiredDimension(dim: DimensionResultDto): boolean {
        return !this.invoiceDetails.requiredDimensions.find((rdim) => rdim.key === dim.dimensionCode);
    }

    downloadSupportDocument(): void {
        if (
            Utils.notNullAndDefined(this.invoiceDetails) &&
            Utils.notNullAndDefined(this.invoiceDetails.supportDocument)
        ) {
            this.helperService.downloadFileUsingUrl(
                this.invoiceDetails.supportDocument.downloadUrl,
                this.invoiceDetails.supportDocument.fileName
            );
        }
    }

    onWaiveClick(): void {
        this.waiveInvoiceConfirmation.show(this.invoiceId, this.selectedContext);
    }

    onWaiveSuccess(e): void {
        this.onSuccess.emit(true);
        this.onHidden(false);
    }

    onCustomerNameClick(data: ARInvoiceResultDto): void {
        if (Utils.notNullAndDefined(this.selectedContext.communityId)) {
            const urlSegments = [
                'property',
                this.selectedContext.communityId,
                this.selectedContext.customerId,
                'dashboard'
            ];
            this.ownerDashboardUrl = urlSegments.join('/');
            window.open(this.ownerDashboardUrl, '_blank');
        } else {
            const urlSegments = [
                'property',
                this.invoiceDetailParam.communityId.toString(),
                data.customerCode,
                'dashboard'
            ];
            this.ownerDashboardUrl = urlSegments.join('/');
            window.open(this.ownerDashboardUrl, '_blank');
        }
    }

    closeCreditApplication(refresh: boolean): void {
        this.showCreditApplication = false;
        if (refresh) {
            this.loadInvoiceDetails();
        }
    }

    showARCreditApplication(): void {
        this.loadingVisible = true;

        this.arCreditApplicationService.getARInvoiceCreditApplications(this.invoiceDetailParam).subscribe(
            (result) => {
                this.creditApplicationData = result;
                this.showCreditApplication = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }

    onWriteOffClick(): void {
        this.showWriteOffInvoice = true;
    }

    onWriteOffInvoiceClose(forceReload: boolean): void {
        this.showWriteOffInvoice = false;
        if (Utils.isTrue(forceReload)) {
            this.onSuccess.emit(true);
            this.onHidden(true);
        }
    }

    onDeleteClick(): void {
        const dialog = this.helperService.getConfirmationDialog(
            'Confirmation',
            '<b> Are you sure you want to delete this Invoice ?</b>'
        );
        dialog.show().then((answer: boolean) => {
            if (answer === true) {
                const params = new ARInvoiceDetailParameterDto();
                params.communityId = this.selectedContext.communityId;
                params.invoiceId = this.invoiceId;
                params.managementCompanyId = this.selectedContext.managementCompanyId;
                params.clearCache = true;
                this.loadingVisible = true;
                this.invoiceDetailsService.deleteARInvoice(params).subscribe(
                    () => {
                        notify('AR Invoice deleted Successfully.', 'success', MessageInterval.successMsgInterval);
                        this.onHidden(true);
                        this.loadingVisible = false;
                    },
                    (error) => {
                        this.serverErrorService.displayError(error);
                        this.onHidden(false);
                        this.loadingVisible = false;
                    }
                );
            }
        });
    }

    onPrintClick(): void {
        const params: ARInvoiceDetailParameterDto = new ARInvoiceDetailParameterDto();
        params.communityId = this.selectedContext.communityId;
        params.invoiceId = this.invoiceId;
        params.managementCompanyId = this.selectedContext.managementCompanyId;
        params.clearCache = true;
        const fileName = `AR_Invoice_${this.invoiceId}_${this.selectedContext.communityId}.pdf`;
        this.loadingVisible = true;
        this.invoiceDetailsService.getPrintARInvoice(params).subscribe(
            (result) => {
                this.helperService.downloadFileUsingBlob(result, fileName);
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    showARManualCreditApplication(): void {
        this.showManualCreditApplication = true;
    }

    onManualCAEditorClose(apply: boolean): void {
        this.showManualCreditApplication = false;
        if (!!apply) {
            this.loadInvoiceDetails();
        }
    }

    onReverseClick(): void {
        this.showReverseEditor = true;
    }

    onReverseEditorClose(forceReload: boolean): void {
        this.showReverseEditor = false;
        if (Utils.isTrue(forceReload)) {
            this.onSuccess.emit(true);
        }
    }

    onTransactionLogClick(): void {
        if (!!this.invoiceId) {
            this.transactionLogTitle = `Log of AR Invoice ${this.invoiceId}`;
            this.populateTransactionLog();
            this.displayTransactionLogPopUp = true;
        }
    }

    onEditClick(): void {
        this.invoiceEditorPopupTitle = `Edit AR Invoice Transaction ${this.invoiceDetailParam.invoiceId}`;
        this.showInvoiceEditor = true;
    }

    onInvoiceEditorClose(refresh: boolean): void {
        this.showInvoiceEditor = false;
        this.invoiceEditorPopupTitle = null;
        if (!!refresh) {
            this.loadInvoiceDetails();
        }
    }

    private populateTransactionLog(): void {
        const baseParam: TransactionLogParameterDto = new TransactionLogParameterDto();
        baseParam.transactionId = this.invoiceId;
        baseParam.clearCache = true;
        this.loadingVisible = true;
        this.invoiceDetailsService.getInvoiceHistory(baseParam).subscribe(
            (result: TransactionLogResultDto[]) => {
                this.transactionLogResultData = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private loadInvoiceDetails(): void {
        this.loadingVisible = true;
        const context: PropertyCommunityInfoResultDto = this.contextManagerService.getSelectedContext();
        if (!!this.invoiceDetailParam) {
            this.selectedContext.communityId = this.invoiceDetailParam.communityId;
            this.selectedContext.managementCompanyId = this.invoiceDetailParam.managementCompanyId;
            this.invoiceId = this.invoiceDetailParam.invoiceId;
        } else if (!!context) {
            this.selectedContext.communityId = context.communityId;
            this.selectedContext.managementCompanyId = this.managementCompanyId;
            this.invoiceDetailParam = new ARInvoiceDetailParameterDto();
            this.invoiceDetailParam.communityId = context.communityId;
            this.invoiceDetailParam.managementCompanyId = this.managementCompanyId;
            this.invoiceDetailParam.invoiceId = this.invoiceId;
        }
        this.invoiceDetailParam.clearCache = true;
        this.hasOtherDimensions = false;
        this.invoiceDetailsService.getInvoiceDetails(this.invoiceDetailParam).subscribe(
            (result: ARInvoiceResultDto) => {
                this.loadingVisible = false;
                if (result.isSuccessMessage) {
                    result.items.forEach((item) => {
                        item.dimensions.forEach((dim) => {
                            const isRequired = !!result.requiredDimensions.find(
                                (rdim) => rdim.key === dim.dimensionCode
                            );
                            if (isRequired) {
                                /*
                                Since we add dynamic columns to the data grid for all required dimensions
                                We are creating a dynamic property under each invoice item with dimension code
                                as property and dimension option title as value: eg: item['Fund'] = 'Operational'
                                This will avoid and help not to create a template for dynamic column to render the information
                                */
                                item[dim.dimensionCode] = dim.dimensionOptionTitle;
                            } else {
                                this.hasOtherDimensions = true;
                            }
                        });
                    });
                    this.invoiceDetails = result;
                    if (!!result.title) {
                        this.popUpTitle = this.invoiceDetails.title;
                    }
                } else if (!!result.message) {
                    this.serverErrorService.updateSubjectErrorMessage(result.message);
                }
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }
}
